import type { VoiceEvaluationServiceTier, VoiceProcessingModelOptions, VoiceProcessingModels } from '../../types';

type Props = {
  models: VoiceProcessingModels;
  options: VoiceProcessingModelOptions;
  labelPrefix: string;
  disabled?: boolean;
  onChange: (models: Partial<VoiceProcessingModels>) => void;
};

const roles = [
  { key: 'evaluation', label: 'answer evaluator' },
  { key: 'safety', label: 'safety classifier' },
  { key: 'safety_shadow', label: 'safety comparison' },
] as const;

export default function VoiceProcessingModelControls({
  models, options, labelPrefix, disabled = false, onChange,
}: Props) {
  const tiersForModel = (model: string): VoiceEvaluationServiceTier[] =>
    options.evaluation.find((option) => option.id === model)?.service_tiers ?? ['default'];
  const serviceTiers = tiersForModel(models.evaluation.model);
  return <div className="space-y-4">
    {roles.map(({ key, label }) => {
      const config = models[key];
      const modelOptions = key === 'evaluation' ? options.evaluation : options.safety;
      const effortsForModel = (model: string) =>
        modelOptions.find((option) => option.id === model)?.reasoning_efforts ?? options.reasoning_efforts;
      const reasoningEfforts = config ? effortsForModel(config.model) : options.reasoning_efforts;
      return <fieldset key={key} disabled={disabled} className="space-y-2">
        <label className="block text-sm">
          <span className="mb-1 block">{labelPrefix} {label}</span>
          <select className="w-full rounded-md border border-input bg-background p-2 text-foreground"
            aria-label={`${labelPrefix} ${label}`} value={config?.model ?? ''}
            onChange={(event) => {
              const model = event.target.value;
              const allowedEfforts = effortsForModel(model);
              const previousEffort = config?.reasoning_effort ?? 'low';
              const previousTier = models.evaluation.service_tier ?? 'default';
              const tierIsSupported = tiersForModel(model).includes(previousTier);
              const hasTierConfig = models.evaluation.service_tier !== undefined
                || options.evaluation.find((option) => option.id === model)?.service_tiers !== undefined;
              onChange({ [key]: model ? {
                model,
                reasoning_effort: allowedEfforts.includes(previousEffort) ? previousEffort : allowedEfforts[0] ?? 'low',
                ...(key === 'evaluation' && hasTierConfig
                  ? { service_tier: tierIsSupported ? previousTier : 'default' } : {}),
              } : null });
            }}>
            {key === 'safety_shadow' ? <option value="">Off</option> : null}
            {config && !modelOptions.some((option) => option.id === config.model)
              ? <option value={config.model}>{config.model}</option> : null}
            {modelOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
          </select>
        </label>
        {config ? <label className="block text-sm">
          <span className="mb-1 block">Reasoning effort</span>
          <select className="w-full rounded-md border border-input bg-background p-2 text-foreground"
            aria-label={`${labelPrefix} ${label} reasoning effort`} value={config.reasoning_effort}
            onChange={(event) => onChange({ [key]: { ...config, reasoning_effort: event.target.value } })}>
            {!reasoningEfforts.includes(config.reasoning_effort)
              ? <option value={config.reasoning_effort}>{config.reasoning_effort}</option> : null}
            {reasoningEfforts.map((effort) => <option key={effort} value={effort}>{effort}</option>)}
          </select>
        </label> : null}
        {key === 'evaluation' ? <label className="block text-sm">
          <span className="mb-1 block">Processing speed</span>
          <select className="w-full rounded-md border border-input bg-background p-2 text-foreground"
            aria-label={`${labelPrefix} ${label} processing speed`}
            value={models.evaluation.service_tier ?? 'default'} disabled={serviceTiers.length < 2}
            onChange={(event) => onChange({ evaluation: {
              ...models.evaluation, service_tier: event.target.value as VoiceEvaluationServiceTier,
            } })}>
            {serviceTiers.map((tier) => <option key={tier} value={tier}>{tier === 'fast' ? 'Fast' : 'Standard'}</option>)}
          </select>
        </label> : null}
      </fieldset>;
    })}
    <p className="text-sm text-muted-foreground">Safety comparison runs alongside the active classifier for measurement only. It cannot change the conversation or trigger a safety action.</p>
  </div>;
}
